import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import { FahrerFormular } from './FahrerFormular'
import { Vertretungssuche } from './Vertretungssuche'
import { AppZugang } from './AppZugang'
import {
  QUALIFIKATION_KURZ,
  fristKlasse,
  type Fahrer,
  type Team,
} from './typen'

interface AnwesenderAntwort {
  benutzerId: number
  /** Leer, wenn hinter dem Zugang kein Fahrer steht — Disposition oder Werkstatt. */
  fahrerId: number | null
  name: string
  betriebshofKuerzel: string | null
  betriebshofName: string | null
  seit: string
  nachweis: string
  nachgewiesen: boolean
}

/** „Seit 05:12" — die Uhrzeit genuegt, laenger als zwoelf Stunden gilt niemand als anwesend. */
function seitWann(zeitpunkt: string): string {
  return new Date(zeitpunkt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

export function Fahrpersonal() {
  const [appZugang, setAppZugang] = useState<Fahrer | null>(null)
  const { hatRolle } = useAuth()
  const darfPflegen = hatRolle('ADMIN', 'PERSONAL')

  const [fahrer, setFahrer] = useState<Fahrer[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [betriebshoefe, setBetriebshoefe] = useState<{ id: number; name: string }[]>([])
  const [kursnummern, setKursnummern] = useState<string[]>([])
  /**
   * Wer gerade auf einem Betriebshof ist, je Fahrerkennung.
   *
   * Getrennt vom App-Zustand daneben: „App gekoppelt" heisst, dass eine Nachricht
   * ankommt, „im Haus" heisst, dass jemand vor Ort ansprechbar ist. Zwei Aussagen,
   * zwei Symbole.
   */
  const [anwesend, setAnwesend] = useState<Map<number, AnwesenderAntwort>>(new Map())
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [bearbeitet, setBearbeitet] = useState<Fahrer | null | undefined>(undefined)
  const [suche, setSuche] = useState('')
  const [nurEinsatzbereit, setNurEinsatzbereit] = useState(false)
  // Der Bestand enthält den ganzen Betrieb. Wer disponiert, will meist nur die Fahrer
  // sehen — wer Stammdaten pflegt, alle. Deshalb ein Umschalter statt einer festen Wahl.
  const [nurFahrer, setNurFahrer] = useState(false)
  // Für den Vergleich mit dem Austritt: Beide Seiten als JJJJ-MM-TT, dann stimmt die
  // Reihenfolge ohne Zeitzonenrechnerei.
  const heuteAlsText = new Date().toISOString().slice(0, 10)
  // Was nach einem eingetragenen Austritt noch eingeteilt ist. Ungefragt wird daran
  // nichts geändert — der Disponent muss umplanen, nicht die Anwendung.
  const [austrittsdienste, setAustrittsdienste] = useState<
    { fahrerId: number; name: string; dienste: { id: number; betriebstag: string; kursnummer: string }[] } | null
  >(null)

  async function pruefeDiensteNachAustritt(fahrerId: number) {
    try {
      const dienste = await api.get<{ id: number; betriebstag: string; kursnummer: string }[]>(
        `/api/fahrpersonal/${fahrerId}/dienste-nach-austritt`,
      )
      if (dienste.length === 0) return
      const f = fahrer.find((x) => x.id === fahrerId)
      setAustrittsdienste({
        fahrerId,
        name: f ? `${f.nachname}, ${f.vorname}` : 'Der Mitarbeiter',
        dienste,
      })
    } catch {
      // Ein gescheiterter Abruf ist kein Grund, das Speichern als misslungen darzustellen.
    }
  }

  async function gibDiensteFrei() {
    if (!austrittsdienste) return
    try {
      await api.post(`/api/fahrpersonal/${austrittsdienste.fahrerId}/dienste-nach-austritt/freigeben`)
      setAustrittsdienste(null)
      void laden()
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Freigeben fehlgeschlagen')
    }
  }
  const [vertretung, setVertretung] = useState(false)

  async function laden() {
    setLaedt(true)
    try {
      const [f, t, b, k, a] = await Promise.all([
        api.get<Fahrer[]>('/api/fahrpersonal'),
        api.get<Team[]>('/api/teams'),
        api.get<{ id: number; name: string }[]>('/api/stammdaten/betriebshoefe'),
        api.get<string[]>('/api/fahrpersonal/kursnummern'),
        api.get<AnwesenderAntwort[]>('/api/anwesenheit'),
      ])
      setFahrer(f)
      setTeams(t)
      setBetriebshoefe(b)
      setKursnummern(k)
      // Nur die mit Fahrerbezug: Diese Liste ist die Fahrerliste. Wer ohne Fahrer im
      // Haus ist, steht unter Betriebshöfe.
      setAnwesend(new Map(a.filter((e) => e.fahrerId != null).map((e) => [e.fahrerId!, e])))
      setFehler(null)
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Laden fehlgeschlagen')
    } finally {
      setLaedt(false)
    }
  }

  useEffect(() => {
    void laden()
  }, [])

  // Verweise aus den Klärungen öffnen die Akte eines Fahrers: /fahrer?id=123. Nur einmal —
  // wer das Formular schließt, will zur Liste und nicht gleich wieder in die Akte.
  const [parameter] = useSearchParams()
  const vorgabeId = Number(parameter.get('id')) || null
  const vorgabeErledigt = useRef(false)
  useEffect(() => {
    // An "fertig geladen" statt an "Liste nicht leer" — sonst bliebe ein Verweis auf einen
    // Fahrer, den es nicht mehr gibt, in einem leeren Bestand stumm.
    if (!vorgabeId || vorgabeErledigt.current || laedt) return
    vorgabeErledigt.current = true
    const f = fahrer.find((x) => x.id === vorgabeId)
    if (f) setBearbeitet(f)
    else setFehler('Der Fahrer aus dem Verweis ist nicht (mehr) im Bestand.')
  }, [vorgabeId, fahrer, laedt])

  const gefiltert = fahrer
    .filter((f) =>
      suche.trim()
        ? `${f.nachname} ${f.vorname} ${f.personalnummer}`
            .toLowerCase()
            .includes(suche.trim().toLowerCase())
        : true,
    )
    .filter((f) => (nurEinsatzbereit ? f.einsatzbereit && !f.heuteAbwesend : true))
    .filter((f) => (nurFahrer ? f.istFahrer : true))

  // undefined = kein Formular, null = neu anlegen, Objekt = bearbeiten
  if (bearbeitet !== undefined) {
    return (
      <>
        <div className="inhalt-kopf">
          <div>
            <h1>Personal</h1>
            <p>{bearbeitet === null ? 'Neuen Mitarbeiter anlegen' : 'Mitarbeiter bearbeiten'}</p>
          </div>
        </div>
        <FahrerFormular
          fahrer={bearbeitet}
          teams={teams}
          betriebshoefe={betriebshoefe}
          kursnummern={kursnummern}
          onGespeichert={(fahrerId) => {
            setBearbeitet(undefined)
            void laden()
            if (fahrerId) void pruefeDiensteNachAustritt(fahrerId)
          }}
          onAbbruch={() => setBearbeitet(undefined)}
        />
      </>
    )
  }

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Personal</h1>
          <p>Alle Mitarbeiter des Betriebs — Fahrer, Werkstatt und Büro</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            className={vertretung ? 'knopf-primaer' : 'knopf-schlicht'}
            onClick={() => setVertretung(!vertretung)}
          >
            Vertretung suchen
          </button>
          {darfPflegen && (
            <button className="knopf-akzent" onClick={() => setBearbeitet(null)}>
              Mitarbeiter anlegen
            </button>
          )}
        </div>
      </div>

      {fehler && <div className="meldung fehler">{fehler}</div>}

      {austrittsdienste && (
        <div className="meldung hinweis">
          <strong>
            {austrittsdienste.name} ist ausgeschieden — {austrittsdienste.dienste.length}{' '}
            {austrittsdienste.dienste.length === 1 ? 'Dienst ist' : 'Dienste sind'} danach noch
            eingeteilt.
          </strong>
          <div style={{ margin: '6px 0', fontSize: '0.88rem' }}>
            {austrittsdienste.dienste.slice(0, 8).map((d) => (
              <span key={d.id} className="etikett grau" style={{ marginRight: 5 }}>
                {new Date(d.betriebstag).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                {' · '}
                {d.kursnummer}
              </span>
            ))}
            {austrittsdienste.dienste.length > 8 && <span>… und {austrittsdienste.dienste.length - 8} weitere</span>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="knopf-primaer" onClick={() => void gibDiensteFrei()}>
              Dienste freigeben
            </button>
            <button className="knopf-schlicht" onClick={() => setAustrittsdienste(null)}>
              Später
            </button>
          </div>
          <div style={{ marginTop: 6, fontSize: '0.82rem', color: 'var(--grau-700)' }}>
            Freigeben nimmt nur den Fahrer heraus; die Kurse stehen danach als unbesetzt im
            Dienstplan und lassen sich neu besetzen.
          </div>
        </div>
      )}

      {vertretung && <Vertretungssuche kursnummern={kursnummern} />}

      {appZugang && (
        <AppZugang
          fahrerId={appZugang.id}
          name={`${appZugang.vorname} ${appZugang.nachname}`.trim()}
          personalnummer={appZugang.personalnummer}
          hatMailadresse={Boolean(appZugang.email)}
          beiSchliessen={() => setAppZugang(null)}
        />
      )}

      <div className="werkzeugleiste">
        <input
          type="text"
          placeholder="Name oder Personalnummer …"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 400, marginBottom: 0 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={nurFahrer}
            onChange={(e) => setNurFahrer(e.target.checked)}
          />
          nur Fahrer
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 400, marginBottom: 0 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={nurEinsatzbereit}
            onChange={(e) => setNurEinsatzbereit(e.target.checked)}
          />
          nur heute einsetzbare
        </label>
        <span style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>
          {gefiltert.length} von {fahrer.length}
          {!nurFahrer && ` · ${fahrer.filter((f) => f.istFahrer).length} Fahrer`}
        </span>
      </div>

      <div className="tabelle-huelle">
        {laedt ? (
          <div className="laden">Wird geladen …</div>
        ) : gefiltert.length === 0 ? (
          <div className="leer">Kein Mitarbeiter gefunden.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Pers.-Nr.</th>
                <th>Name</th>
                <th>Tätigkeit</th>
                <th>Team</th>
                <th>Kurse</th>
                <th>Qualifikationen</th>
                <th>App</th>
                <th>Vor Ort</th>
                <th>Zustand</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {gefiltert.map((f) => (
                <tr key={f.id}>
                  <td className="zahl">{f.personalnummer}</td>
                  <td>
                    <strong>
                      {f.nachname}, {f.vorname}
                    </strong>
                  </td>
                  <td>
                    {f.istFahrer ? (
                      <span className="etikett gruen">Fahrer</span>
                    ) : (
                      <span className="etikett grau">fährt nicht</span>
                    )}
                  </td>
                  <td>
                    {f.teamName ? (
                      <span
                        className="etikett navy"
                        style={f.teamFarbe ? { background: f.teamFarbe, color: '#fff' } : undefined}
                      >
                        {f.teamName}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--grau-500)' }}>—</span>
                    )}
                  </td>
                  <td>
                    <span title={f.einsetzbarAufKursen.join(', ')}>
                      {f.einsetzbarAufKursen.length === 0 ? (
                        <span style={{ color: 'var(--grau-500)' }}>keine</span>
                      ) : (
                        <>
                          <strong>{f.einsetzbarAufKursen.length}</strong>{' '}
                          <span style={{ color: 'var(--grau-500)', fontSize: '0.82rem' }}>
                            {f.einsetzbarAufKursen.slice(0, 4).join(', ')}
                            {f.einsetzbarAufKursen.length > 4 && ' …'}
                          </span>
                        </>
                      )}
                    </span>
                  </td>
                  <td>
                    {f.qualifikationen.map((q) => (
                      <span
                        key={q.id}
                        className={`etikett ${fristKlasse(q.gueltig, q.tageBisAblauf)}`}
                        style={{ marginRight: 4 }}
                        title={
                          q.gueltigBis
                            ? `gültig bis ${q.gueltigBis}`
                            : 'unbefristet'
                        }
                      >
                        {QUALIFIKATION_KURZ[q.typ]?.split(' ')[0] ?? q.typ}
                      </span>
                    ))}
                  </td>
                  <td>
                    {/* Wer die App tatsaechlich in Betrieb hat — bei 82 Fahrern sonst
                        nicht zu erkennen, wer noch einen Ausdruck braucht. */}
                    {f.app?.geraetGekoppelt ? (
                      <span
                        className="etikett gruen"
                        title={
                          (f.app.geraetename ? f.app.geraetename + ' · ' : '') +
                          'aktiviert am ' +
                          (f.app.aktiviertAm
                            ? new Date(f.app.aktiviertAm).toLocaleDateString('de-DE')
                            : '–') +
                          (f.app.letzteNutzung
                            ? ', zuletzt ' +
                              new Date(f.app.letzteNutzung).toLocaleDateString('de-DE')
                            : ', noch nicht benutzt')
                        }
                      >
                        aktiv
                      </span>
                    ) : f.app?.codeOffen ? (
                      <span className="etikett orange" title="Code ausgegeben, noch nicht eingelöst">
                        Code offen
                      </span>
                    ) : (
                      <span style={{ color: 'var(--grau-500)' }}>–</span>
                    )}
                  </td>
                  <td>
                    {/* Anwesenheit vom Betriebshof. Ein nicht nachgewiesener Eintrag wird
                        angezeigt, aber als solcher gekennzeichnet: Wer da ist, soll
                        sichtbar sein, und wer es nur behauptet, unterscheidbar. */}
                    {(() => {
                      const a = anwesend.get(f.id)
                      if (!a) return <span style={{ color: 'var(--grau-500)' }}>–</span>
                      return (
                        <span
                          className={`etikett ${a.nachgewiesen ? 'gruen' : 'orange'}`}
                          title={
                            (a.betriebshofName ?? 'Betriebshof unbekannt') +
                            ' · seit ' +
                            seitWann(a.seit) +
                            (a.nachgewiesen ? '' : ' · ohne Nachweis')
                          }
                        >
                          im Haus {seitWann(a.seit)}
                        </span>
                      )
                    })()}
                  </td>
                  <td>
                    {!f.aktiv || (f.austritt && f.austritt < heuteAlsText) ? (
                      <span className="etikett grau" title={f.austritt ? `ausgeschieden zum ${new Date(f.austritt).toLocaleDateString('de-DE')}` : undefined}>
                        ausgeschieden
                      </span>
                    ) : f.heuteAbwesend ? (
                      <span className="etikett orange">heute abwesend</span>
                    ) : f.einsatzbereit ? (
                      <span className="etikett gruen">einsatzbereit</span>
                    ) : (
                      <span className="etikett rot">gesperrt</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {darfPflegen && (
                      <>
                        <button
                          className="knopf-schlicht"
                          style={{ padding: '5px 12px' }}
                          onClick={() => setAppZugang(f)}
                        >
                          QR-Code
                        </button>{' '}
                      </>
                    )}
                    <button
                      className="knopf-schlicht"
                      style={{ padding: '5px 12px' }}
                      onClick={() => setBearbeitet(f)}
                    >
                      {darfPflegen ? 'Bearbeiten' : 'Ansehen'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}
